import { Component, ViewChild, OnInit, Inject } from '@angular/core';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { PresignedUrlService } from 'src/core/services/presigned-url.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { EditOrderFormData, Order } from 'src/interfaces/order';
import { formatDistance } from 'date-fns';
import { MatSort } from '@angular/material/sort';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { Sort } from '@angular/material/sort';
import { OrderStatus } from 'src/interfaces/order-status';
import { UntypedFormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ShippingDetails } from 'src/interfaces/shipping-details';
import { OrderItem } from 'src/interfaces/order-item';
import { IndividualService } from 'src/core/services/individual.service';
import { CompanyService } from 'src/core/services/company.service';
import { MatStepper } from '@angular/material/stepper';
import { format } from 'date-fns';

export interface DialogData {
  order: Order;
  title: string;
  message: string;
}
@Component({
    selector: 'app-prescription-form',
    templateUrl: './prescription-form.component.html',
    styleUrls: ['./prescription-form.component.scss'],
    standalone: false
})
export class PrescriptionFormComponent implements OnInit {
  displayedColumns: string[] = [
    'id',
    'name',
    'status',
    'contact',
    'created_at',
    'updated_at',
  ];

  ordersDataSource = new MatTableDataSource<Order>([]);
  page: number = 1;
  limit: number = 10;
  totalNumberOfOrders: number = 0;
  orderStatuses: OrderStatus[] = [];
  selectedStatus: OrderStatus | null = null;
  sortDirection: string = 'desc';
  orderBy: string = 'created_at';
  currentOrder: Order | null = null;
  fetchedOrder: Order | null = null;
  orderFilterForm = this._formBuilder.group({
    status: [''],
    search: [''],
  });
  waybillCompleted: boolean = false;
  @ViewChild('stepper') private _stepper: MatStepper;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private prescriptionService: PrescriptionService,
    private router: Router,
    private route: ActivatedRoute,
    private presignedUrlService: PresignedUrlService,
    private localStorageService: LocalStorageService,
    private _formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private _individualService: IndividualService,
    private _companyService: CompanyService
  ) {}
  async ngOnInit(): Promise<void> {
    this.orderFilterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe((searchValue) => {
        this.page = 1;
        void this.listOrders(
          this.page,
          this.limit,
          this.sortDirection,
          null,
          this.orderBy,
          searchValue
        );
      });

    const limit = this.localStorageService.get('ORDERS', 'LIMIT');
    this.currentOrder = this.data.order;
    await this.listOrders();

    if (this.paginator) {
      this.ordersDataSource.paginator = this.paginator;
    }

    if (limit) {
      this.limit = parseInt(limit);
    }
  }

  resetStatus(event: Event) {
    event.stopPropagation();
    this.selectedStatus = null;
    this.orderFilterForm.patchValue({
      status: null,
    });
  }

  onPageChange(event: PageEvent) {
    this.page = event.pageIndex + 1; 
    this.limit = event.pageSize;

    
    this.localStorageService.store('ORDERS', 'LIMIT', this.limit.toString());
    this.localStorageService.store('ORDERS', 'PAGE', this.page.toString());

    
    this.listOrders(
      this.page,
      this.limit,
      this.sortDirection,
      null,
      this.orderBy,
      this.orderFilterForm.value.search
    );
  }

  async listOrders(
    page: number = 1,
    limit: number = 10,
    order: string = this.sortDirection,
    status: string | null = null,
    orderBy: string = this.orderBy,
    search: string | null = null
  ) {
    const newOrders = await this.prescriptionService.getOrders(
      page,
      limit,
      order,
      status,
      orderBy,
      search
    );
    const newData = newOrders.filter(
      (listedOrder) => listedOrder.id !== this.currentOrder?.id
    );
    this.ordersDataSource.data = newData;

    this.totalNumberOfOrders =
      this.prescriptionService.getTotalNumberOfOrders();

    
    this.ordersDataSource.paginator = this.paginator;
    this.ordersDataSource.sort = this.sort;
  }

  
  goToNextPage() {
    if (this.page < this.paginator.getNumberOfPages()) {
      this.onPageChange({
        pageIndex: this.page,
        pageSize: this.limit,
        length: this.totalNumberOfOrders,
      });
    }
  }

  
  goToPreviousPage() {
    if (this.page > 1) {
      this.onPageChange({
        pageIndex: this.page - 2,
        pageSize: this.limit,
        length: this.totalNumberOfOrders,
      });
    }
  }

  async sortOrders(sort: Sort) {
    switch (sort.active) {
      case 'status':
        this.orderBy = 'status_id';
        break;
      case 'created_at':
        this.orderBy = 'created_at';
        this.orderBy = 'created_at';
        break;
      case 'updated_at':
        this.orderBy = 'updated_at';
        break;

      default:
        this.orderBy = 'created_at';
        break;
    }
    this.sortDirection = sort.direction;
    this.listOrders(
      this.page,
      this.limit,
      this.sortDirection,
      null,
      this.orderBy,
      this.orderFilterForm.value.search
    );
  }

  async openOrder(order: Order) {
    try {
      this.fetchedOrder = await this.prescriptionService.getOrder(order.id);

      if (this.fetchedOrder) {
        this._stepper.next();
      } else {
        console.warn('Fetched order is null or undefined');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  }

  getOwnerTitle(order: Order) {
    if (order.patient) return order.patient.title;

    if (order.client) return order.client.name;

    if (order.doctor) return order.doctor.title;

    return '';
  }

  getEntityName(order: Order) {
    if (order.patient) {
      if (order.patient.company) return order.patient.company.name;
      return order.patient.first_name + ' ' + order.patient.last_name;
    }

    if (order.client) return order.client.name;

    if (order.doctor)
      return order.doctor.first_name + ' ' + order.doctor.last_name;

    return '';
  }

  getOwnerPhoneNumber(order: Order) {
    if (order.patient) return order.patient.phone_number;

    if (order.client) return order.client.phone_number;

    if (order.doctor) return order.doctor.phone_number;

    return '';
  }

  async openPrescriptionFile(event: Event, order: Order) {
    event.stopPropagation();

    const id: number = order.parent_order_id ? order.parent_order_id : order.id;

    try {
      const { region, bucket, folder, fileName } = environment.s3;
      const key = `${folder}/${id}/${fileName}`;
      const response = await this.presignedUrlService.generateDownloadUrl(
        region,
        bucket,
        key
      );

      window.open(response.data.url, '_blank')?.focus();
    } catch (error) {
      console.error(error);

      return;
    }
  }

  getCreationTime(order: Order) {
    return format(new Date(order.created_at), 'MMMM dd, yyyy HH:mm');
  }

  formatTime(time: string | null) {
    if (!time) return '-';
    return format(new Date(time), 'MMMM dd, yyyy HH:mm');
  }

  isMedicineAssigned(order: Order): boolean {
    return order.number_of_items > 0;
  }

  isEntityAssigned(order: Order): boolean {
    return (
      order.patient !== null || order.doctor !== null || order.client !== null
    );
  }

  getBadgeBackground(status: OrderStatus) {
    switch (status.code) {
      case 'AWR': // Awaiting review
        return 'bg-dark';
      case 'AWF': // Awaiting fulfilment
        return 'bg-info';
      case 'PKG': // Packaging
        return 'bg-primary';
      case 'SHP': // Shipped
        return 'bg-success';

      default:
        return 'bg-secondary';
    }
  }

  async editOrder(): Promise<boolean> {
    const order: Order | null = this.currentOrder;
    if (!order || !this.fetchedOrder) return false;
    const shippingDetails = this.fetchedOrder.shipping_details;
    const orderItems: OrderItem[] =
      this.prescriptionService.getOrderItems(order);

    const medicine = orderItems.map((item) => ({
      id: item.id ? item.id : undefined, // a new order item has an id of undefined
      quantity: item.quantity,
      medicine_dosage_package_type_id: item.medicineDosagePackageType.id,
      order_item_status_id: item.status ? item.status.id : null,
      deleted_at: item.deleted_at ? item.deleted_at : undefined,
    }));

    const payload: EditOrderFormData = {
      id: order.id,
      barcode: order.barcode,
      created_at: order.created_at,
      updated_at: order.updated_at,
      deleted_at: order.deleted_at,
      order_status_id: order.order_status_id,
      client_id: order.client_id ? order.client_id : undefined,
      doctor_id: order.doctor_id,
      patient_id: order.patient_id,
      shipping_details_id: this.fetchedOrder.shipping_details_id,
      parent_order_id: order.parent_order_id,
      doctor: order.doctor
        ? this._individualService.constructIndividualEditPayload(order.doctor)
        : undefined,
      client: order.client
        ? this._companyService.constructionCompanyEditpayload(
            order.client,
            null
          )
        : undefined,
      status: order.status,
      patient: order.patient
        ? this._individualService.constructIndividualEditPayload(order.patient)
        : undefined,
      shipping_details: {
        id: shippingDetails.id,
        waybill_number: shippingDetails.waybill_number,
        address_id: shippingDetails.address_id,
        delivery_type_id: shippingDetails.delivery_type_id,
        address: shippingDetails.address,
        waybill_data: shippingDetails.waybill_data,
      },
      medicine,
      prescriber_id: order.prescriber_id ? order.prescriber_id : undefined,
      prescriber: order.prescriber
        ? this._individualService.constructIndividualEditPayload(
            order.prescriber
          )
        : undefined,
    };

    const success = await this.prescriptionService.editOrder(payload, true);
    return success;
  }
}
